// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.
//
// Where to send the user once a Keycloak sign-in completes. Written right before the app hands off
// to Keycloak (session expiry bounce, or an explicit `returnTo`), consumed by whichever leg finishes
// the login. Web keeps it in sessionStorage so it survives the full-page redirect; native uses
// AsyncStorage because React Native defines `window` but no Web Storage.

import type { Href } from 'expo-router';

type AsyncStorageLike = {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
  removeItem: (key: string) => Promise<void>;
};

let asyncStorage: AsyncStorageLike | null = null;
try {
  asyncStorage = require('@react-native-async-storage/async-storage').default as AsyncStorageLike;
} catch {
  asyncStorage = null;
}

export const POST_LOGIN_PATH_KEY = 'goffy:post-login-path';

const getSessionStorage = () => (typeof window !== 'undefined' ? window.sessionStorage : undefined);

// Only in-app paths, and never one of the auth screens themselves — landing back on /callback or
// /login after signing in would loop, and an absolute URL would be an open redirect.
const BLOCKED_PREFIXES = ['/login', '/callback', '/register'];

export const sanitizePostLoginPath = (value: string | null | undefined): string | null => {
  const path = typeof value === 'string' ? value.trim() : '';
  if (!path.startsWith('/') || path.startsWith('//')) return null;
  const lower = path.toLowerCase();
  if (BLOCKED_PREFIXES.some((prefix) => lower === prefix || lower.startsWith(`${prefix}/`) || lower.startsWith(`${prefix}?`))) {
    return null;
  }
  return path;
};

export const storePostLoginPath = async (path: string | null | undefined): Promise<void> => {
  const safe = sanitizePostLoginPath(path);
  const sessionStorage = getSessionStorage();
  if (sessionStorage) {
    if (safe) sessionStorage.setItem(POST_LOGIN_PATH_KEY, safe);
    else sessionStorage.removeItem(POST_LOGIN_PATH_KEY);
    return;
  }
  if (asyncStorage) {
    if (safe) await asyncStorage.setItem(POST_LOGIN_PATH_KEY, safe);
    else await asyncStorage.removeItem(POST_LOGIN_PATH_KEY);
  }
};

// The stored value is validated by sanitizePostLoginPath to be an in-app path, so it is safe to
// hand to router.replace/<Redirect> as a Href; expo-router's typed routes only know the app's own
// static route table, not a value that arrived through storage at runtime.
const asHref = (path: string | null): Href | null => path as Href | null;

/** Web only: read without removing, for synchronous `<Redirect>` fallbacks. Native returns null. */
export const peekPostLoginPathSync = (): Href | null =>
  asHref(sanitizePostLoginPath(getSessionStorage()?.getItem(POST_LOGIN_PATH_KEY)));

/** Read and clear. */
export const consumePostLoginPath = async (): Promise<Href | null> => {
  const sessionStorage = getSessionStorage();
  if (sessionStorage) {
    const value = sessionStorage.getItem(POST_LOGIN_PATH_KEY);
    sessionStorage.removeItem(POST_LOGIN_PATH_KEY);
    return asHref(sanitizePostLoginPath(value));
  }
  if (asyncStorage) {
    const value = await asyncStorage.getItem(POST_LOGIN_PATH_KEY);
    await asyncStorage.removeItem(POST_LOGIN_PATH_KEY);
    return asHref(sanitizePostLoginPath(value));
  }
  return null;
};
